import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  InputAdornment,
  Avatar,
  Tooltip,
  useMediaQuery,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Search, Block, CheckCircle, Download } from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { ConfirmDialog } from '@/components/common';
import { adminUserService, AdminUser, AdminUserParams } from '@/services/admin/adminUserService';
import toast from 'react-hot-toast';

export const UsersPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchFilter, setSearchFilter] = useState('');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState<string>('all');
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [_, setActionLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [searchFilter, emailVerifiedFilter, paginationState]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: AdminUserParams = {
        page: paginationState.pageIndex + 1,
        limit: paginationState.pageSize,
        sort: '-createdAt',
      };

      if (searchFilter) {
        params.search = searchFilter;
      }

      if (emailVerifiedFilter !== 'all') {
        params.isEmailVerified = emailVerifiedFilter === 'verified';
      }

      const response = await adminUserService.getUsers(params);

      if (response.success) {
        setUsers(response.data.users);
        setTotalItems(response.data.pagination.totalItems);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
      toast.error(err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBlockClick = (user: AdminUser) => {
    setSelectedUser(user);
    setBlockDialogOpen(true);
  };

  const handleUnblockClick = (user: AdminUser) => {
    setSelectedUser(user);
    setUnblockDialogOpen(true);
  };

  const handleBlockConfirm = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const response = await adminUserService.blockUser(selectedUser._id);
      if (response.success) {
        toast.success(response.message || 'User blocked successfully');
        loadUsers();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to block user');
    } finally {
      setActionLoading(false);
      setBlockDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleUnblockConfirm = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const response = await adminUserService.unblockUser(selectedUser._id);
      if (response.success) {
        toast.success(response.message || 'User unblocked successfully');
        loadUsers();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to unblock user');
    } finally {
      setActionLoading(false);
      setUnblockDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleDialogCancel = () => {
    setBlockDialogOpen(false);
    setUnblockDialogOpen(false);
    setSelectedUser(null);
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      const params: AdminUserParams = {};
      if (searchFilter) params.search = searchFilter;
      if (emailVerifiedFilter !== 'all') {
        params.isEmailVerified = emailVerifiedFilter === 'verified';
      }

      const blob = await adminUserService.exportUsers(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix =
        emailVerifiedFilter === 'verified'
          ? '_verified'
          : emailVerifiedFilter === 'not_verified'
            ? '_unverified'
            : '_all';
      link.setAttribute('download', `users${suffix}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Users exported successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<AdminUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'User',
        size: isMobile ? 150 : 200,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}
            >
              {row.original.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.userId}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: isMobile ? 150 : 200,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'whatsappNumber',
        header: 'WhatsApp',
        size: 130,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'designation',
        header: 'Designation',
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>() || '-'}
          </Typography>
        ),
      },
      {
        accessorKey: 'isEmailVerified',
        header: 'Email Verified',
        size: 130,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Verified' : 'Not Verified'}
            color={cell.getValue() ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Active' : 'Blocked'}
            color={cell.getValue() ? 'success' : 'error'}
            size="small"
          />
        ),
      },
      {
        accessorKey: 'membershipTier',
        header: 'Tier',
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue<string>()?.toUpperCase() || 'FREE'}
            color={cell.getValue<string>() === 'premium' ? 'primary' : 'default'}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined',
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {new Date(cell.getValue<string>()).toLocaleDateString()}
          </Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {row.original.isActive ? (
              <Tooltip title="Block User">
                <IconButton
                  size="small"
                  onClick={() => handleBlockClick(row.original)}
                  sx={{ color: 'error.main' }}
                >
                  <Block fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Unblock User">
                <IconButton
                  size="small"
                  onClick={() => handleUnblockClick(row.original)}
                  sx={{ color: 'success.main' }}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [isMobile]
  );

  const table = useMaterialReactTable({
    columns,
    data: users,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: totalItems,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Chip
          label={`${totalItems} Users`}
          color="primary"
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleErrorDismiss}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              select
              size="small"
              label="Email Verified"
              value={emailVerifiedFilter}
              onChange={(e) => setEmailVerifiedFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="not_verified">Not Verified</MenuItem>
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 'auto' }} sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCsv}
              disabled={isExporting}
              sx={{ textTransform: 'none' }}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <MaterialReactTable table={table} />

      <ConfirmDialog
        open={blockDialogOpen}
        title="Block User"
        message={`Are you sure you want to block "${selectedUser?.name}"? This user will not be able to access the platform.`}
        confirmText="Block"
        onConfirm={handleBlockConfirm}
        onCancel={handleDialogCancel}
        severity="error"
      />

      <ConfirmDialog
        open={unblockDialogOpen}
        title="Unblock User"
        message={`Are you sure you want to unblock "${selectedUser?.name}"? This user will regain access to the platform.`}
        confirmText="Unblock"
        onConfirm={handleUnblockConfirm}
        onCancel={handleDialogCancel}
        severity="info"
      />
    </Box>
  );
};

export default UsersPage;
